import {
    FormField,
    FormFieldCheckbox,
    FormFieldSelect,
    FormFieldTime,
} from '@/global/components/Form'
import IconeAjuda from '@/global/components/IconeAjuda'
import Grid from '@/global/components/ui/Grid'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

/** Horário noturno legal de cada ramo — usado para preencher os campos de horário. */
const HORARIOS_POR_RAMO: Record<number, { inicio: string; fim: string }> = {
    1: { inicio: '22:00', fim: '05:00' },
    2: { inicio: '21:00', fim: '05:00' },
    3: { inicio: '20:00', fim: '04:00' },
}

export default function FormNoturnoRegraCalculo() {
    const { watch, setValue } = useFormContext()

    const [temAdicionalNoturno, inicio, fim, reducaoAdicionalNoturno] = watch([
        'temAdicionalNoturno',
        'horarioInicioAdicionalNoturno',
        'horarioFimAdicionalNoturno',
        'reducaoAdicionalNoturno',
    ])

    useEffect(() => {
        if (inicio == '22:00' && fim == '05:00') {
            setValue('tmp_ramo', 1)
        } else if (inicio == '21:00' && fim == '05:00') {
            setValue('tmp_ramo', 2)
        } else if (inicio == '20:00' && fim == '04:00') {
            setValue('tmp_ramo', 3)
        } else {
            setValue('tmp_ramo', -1)
        }
    }, [inicio, fim])

    useEffect(() => {
        if (reducaoAdicionalNoturno) {
            setValue('exibirHorasNoturnasReduzidas', false)
        }
    }, [reducaoAdicionalNoturno])

    return (
        <Grid container className="pt-8">
            <Grid item sm={4} xs={7}>
                <FormFieldCheckbox
                    name="temAdicionalNoturno"
                    label="Tem adicional noturno"
                    endAdornment={
                        <IconeAjuda>
                            Se esta opção estiver habilitada, o adicional
                            noturno será calculado seguindo o horário noturno
                            informado, caso contrário, o adicional noturno não
                            será calculado nem exibido no espelho de ponto.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={2} xs={5}>
                <FormField
                    name="adicionalNoturno"
                    type="number"
                    label="Porcentagem"
                    required
                    endAdornment={
                        <div className="inline-flex items-baseline">
                            <span>%</span>
                            <IconeAjuda>
                                Porcentagem do adicional noturno que será
                                exportada na folha de pagamento.
                            </IconeAjuda>
                        </div>
                    }
                    min={0}
                    max={100}
                />
            </Grid>
            <Grid item xs={6}>
                <FormFieldSelect
                    name="tmp_ramo"
                    label="Ramo do adicional noturno"
                    disabled={!temAdicionalNoturno}
                    options={[
                        { value: -1, label: 'Outro ramo' },
                        { value: 1, label: 'Urbano' },
                        { value: 2, label: 'Agrícola' },
                        { value: 3, label: 'Pecuária' },
                    ]}
                    onChange={ramo => {
                        const horarios = HORARIOS_POR_RAMO[ramo]
                        if (!horarios) return

                        setValue(
                            'horarioInicioAdicionalNoturno',
                            horarios.inicio,
                        )
                        setValue('horarioFimAdicionalNoturno', horarios.fim)
                    }}
                    endAdornment={
                        <IconeAjuda>
                            Na legislação trabalhista, o horário noturno é
                            dividido da seguinte maneira:
                            <br />
                            <br />
                            <table className="border-collapse border rounded-sm [&_th]:border [&_td]:border">
                                <thead>
                                    <tr className="*:px-2">
                                        <th>Ramo</th>
                                        <th>Horário noturno</th>
                                        <th>Redução</th>
                                    </tr>
                                </thead>
                                <tbody className="[&_tr>td]:px-3">
                                    <tr>
                                        <td>Urbano</td>
                                        <td>22h às 5h</td>
                                        <td>1h = 52min30s</td>
                                    </tr>
                                    <tr>
                                        <td>Rural</td>
                                        <td>21h às 5h</td>
                                        <td>Não se aplica</td>
                                    </tr>
                                    <tr>
                                        <td>Pecuária</td>
                                        <td>20h às 4h</td>
                                        <td>Não se aplica</td>
                                    </tr>
                                </tbody>
                            </table>
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={4} xs={6}>
                <FormFieldTime
                    name="horarioInicioAdicionalNoturno"
                    label="Início do adicional noturno"
                    required
                    disabled={!temAdicionalNoturno}
                    endAdornment={
                        <IconeAjuda>
                            O horário de início do adicional noturno deve ser
                            preenchido com o horário que varia de acordo com o
                            ramo da empresa. Por exemplo, no ramo urbano, o
                            horário de início é às 22h.
                            <br />
                            Registros marcados após esse horário dão início ao
                            adicional noturno.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={4} xs={6}>
                <FormFieldTime
                    name="horarioFimAdicionalNoturno"
                    label="Fim do adicional noturno"
                    required
                    disabled={!temAdicionalNoturno}
                    endAdornment={
                        <IconeAjuda>
                            O horário de fim do adicional noturno deve ser
                            preenchido com o horário que varia de acordo com o
                            ramo da empresa. Por exemplo, no ramo urbano, o
                            horário de fim é às 5h.
                            <br />
                            Registros marcados antes desse horário dão início ao
                            adicional noturno.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={4} xs={6}>
                <FormFieldTime
                    name="horarioEncerramentoAdicionalNoturno"
                    label="Fim da extensão"
                    disabled={!temAdicionalNoturno}
                    endAdornment={
                        <IconeAjuda>
                            Caso o empregado tenha o início de sua jornada de
                            trabalho no horário diurno, ou seja, antes do{' '}
                            <strong>Início do adicional noturno</strong>, e sua
                            jornada seja estendida após o{' '}
                            <strong>Fim do adicional noturno</strong>, terá
                            direito ao adicional noturno até o fim da sua
                            jornada de trabalho.
                            <br />
                            Deixe este campo em branco para que a extensão do
                            adicional noturno seja até o fim da jornada.
                            <br />
                            Preencha este campo para determinar um horário fixo
                            para o fim da extensão do adicional noturno, mesmo
                            que a jornada ultrapasse esse horário.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={5} xs={12}>
                <FormFieldCheckbox
                    name="reducaoHrsATrabalhar"
                    label="Redução de horas a trabalhar"
                    endAdornment={
                        <IconeAjuda>
                            Reduz a carga prevista da tabela de horário no
                            período noturno: cada 52 minutos e 30 segundos
                            contam como 1 hora a trabalhar.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="reducaoHrsTrabalhadas"
                    label="Redução sobre horas trabalhadas"
                    endAdornment={
                        <IconeAjuda>
                            Reduz as horas trabalhadas, as extras e as faltas no
                            período noturno: cada 52 minutos e 30 segundos
                            trabalhados contam como 1 hora. Marque as duas
                            opções para reduzir a carga prevista e as horas
                            trabalhadas.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormFieldCheckbox
                    name="reducaoAdicionalNoturno"
                    label="Redução sobre adicional noturno"
                    endAdornment={
                        <IconeAjuda>
                            Se esta opção estiver habilitada, a redução de horas
                            será aplicada sobre o adicional noturno, ou seja, a
                            cada 52 minutos e 30 segundos trabalhados no período
                            noturno, será contabilizado 1 hora de adicional
                            noturno.
                        </IconeAjuda>
                    }
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                {/* TODO: substituir pelo novo checkbox depois do merge */}
                <div className="flex items-center w-full select-none">
                    <FormFieldCheckbox
                        name="exibirHorasNoturnasReduzidas"
                        label="Exibir horas noturnas reduzidas"
                        disabled={!!reducaoAdicionalNoturno}
                    />
                    <IconeAjuda>
                        Cria no espelho de ponto uma coluna com{' '}
                        <strong>apenas o acréscimo</strong> da redução da hora
                        noturna. O adicional noturno continua saindo sem
                        redução, e o acréscimo vai para a folha em rubrica
                        própria.
                        <br />
                        <br />
                        Indisponível enquanto{' '}
                        <strong>Redução sobre adicional noturno</strong> estiver
                        marcada: nesse caso a redução já está embutida no
                        adicional, e somar as duas pagaria o mesmo tempo duas
                        vezes.
                    </IconeAjuda>
                </div>
            </Grid>
        </Grid>
    )
}
